import { useState, useEffect, useRef, useCallback } from "react";
import { XIcon } from "@heroicons/react/outline";
import Input from "../ui/input";
import { ScrollArea } from "../ui/scrollArea";
import Button from "../ui/Button/Button.jsx";
import { Plus, Search, Calendar, Save, X, Bookmark } from "lucide-react";
import NewEventForm from "./NewEventForm";
import EventImage from "../../Assets/Images/ColoredGiftBg.png";
import EventImageInverse from "../../Assets/Images/ColoredGiftBgInverse.png";
import useFavoriteStore from "../../Store/FavoriteStore";
import Textarea from "../ui/Textarea";
import { createPortal } from "react-dom";
import PillButton from "../ui/Button/PillButton.jsx";
import { useTranslation } from "react-i18next";

const EventDropdown = ({
  giftID,
  giftImage,
  events,
  spaces,
  selectedEvent,
  setSelectedEvent,
  setEventOpen,
  setOpenComponentID,
  removeProductFromEvent,
  addProductToEvent,
  handleFetchEvents,
  openButtonRef,
}) => {
  const { t } = useTranslation();
  const componentRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newEventIsOpen, setNewEventIsOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupVisible, setPopupVisible] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState(events);
  const [filteredSpaces, setFilteredSpaces] = useState(spaces);
  const [errorMessage, setErrorMessage] = useState("");

  const [position, setPosition] = useState("right-0");
  const { setPopup, handleAddingNewSpaceOrEvent } = useFavoriteStore();
  const [isAddingspace, setIsAddingspace] = useState(false);
  const [spaceContent, setspaceContent] = useState("");

  const upcomingEvents = filteredEvents
    .filter((event) => new Date(event.eventDate) >= new Date())
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    .slice(0, 2);
  const allEvents = filteredEvents.filter(
    (event) => !upcomingEvents.includes(event)
  );

  useEffect(() => {
    setFilteredEvents(
      events.filter((event) =>
        event.eventName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, events]);

  useEffect(() => {
    handleFetchEvents();
    const adjustPosition = () => {
      if (componentRef.current) {
        const { right, left } = componentRef.current.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        setPosition(
          right > screenWidth ? "right-0" : left < 0 ? "left-0" : "right-0"
        );
      }
    };
    adjustPosition();
    window.addEventListener("resize", adjustPosition);
    return () => window.removeEventListener("resize", adjustPosition);
  }, [componentRef]);

  const handleEventClick = (type, name) => {
    if (selectedEvent === name) {
      setSelectedEvent(null);
      setEventOpen(false);
      removeProductFromEvent(type, name, giftID);
      setOpenComponentID(null);
      setPopup(t("gift.removedFrom"), "", name, type);
    } else {
      setSelectedEvent(name);
      setEventOpen(false);
      addProductToEvent(type, name, giftID);
      setOpenComponentID(null);
      setPopup(t("gift.addedTo"), giftImage, name, type);
    }
  };

  const handleCreateEvent = () => {
    setNewEventIsOpen(true);
  };

  const handleClickOutside = useCallback(
    (event) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target) &&
        openButtonRef.current &&
        !openButtonRef.current.contains(event.target) &&
        !newEventIsOpen
      ) {
        handleClose();
      }
    },
    [componentRef, openButtonRef, newEventIsOpen]
  );

  const handleClose = () => {
    if (!newEventIsOpen) {
      setEventOpen(false);
      setOpenComponentID(null);
    }
  };

  const handleSavespace = async () => {
    if (spaceContent.trim()) {
      const spaceDetails = { spaceName: spaceContent };
      try {
        const response = await handleAddingNewSpaceOrEvent(
          "spaces",
          [],
          spaceDetails
        );
        if (response.status === 400) {
          setErrorMessage(
            t("eventsAndSpaces.spaceAlreadyExists")
          );
        } else {
          setErrorMessage("");
          setspaceContent("");
          setIsAddingspace(false);
          handleEventClick("spaces", spaceContent);
        }
      } catch (error) {
        console.error("Error:", error);
      }
    }
  };

  const handleCancelspace = () => {
    setspaceContent("");
    setIsAddingspace(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (popupVisible) {
      const timer = setTimeout(() => setPopupVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [popupVisible]);

  return (
    <div
      ref={componentRef}
      className={`absolute w-[300px] bg-grey-500 rounded-3xl shadow-lg border border-gray-200 ${position} z-1 `}
    >
      <div className="p-4  bg-gradient-to-r from-red-50 to-gray-100 rounded-t-3xl">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-neutral-green-700">
            {t("gift.saveToEvent")}
          </h2>
        </div>
        <div className="relative flex items-start">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder={t("eventsAndSpaces.searchEvents")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-2 py-2.5 text-sm rounded-full bg-white"
          />
        </div>
      </div>

      <ScrollArea className="max-h-[220px] px-4 py-3">
        {upcomingEvents.length > 0 && (
          <>
            <h3 className="flex items-start text-xs font-semibold text-gray-500 mb-2 ">
              {t("eventsAndSpaces.upcomingEvents")}
            </h3>
            {upcomingEvents.map((event, eventName) =>
              EventItems(event, eventName, selectedEvent, handleEventClick)
            )}
          </>
        )}

        {allEvents.length > 0 && (
          <>
            <h3 className="flex items-start text-xs font-semibold text-gray-500 mb-2 ">
              {t("navigation.events")}
            </h3>
            {allEvents.map((event, eventName) =>
              EventItems(event, eventName, selectedEvent, handleEventClick)
            )}
          </>
        )}
      </ScrollArea>

      <div className="p-4 border-t border-gray-200">
        <Button
          variant="primary"
          size="md"
          rounded="full"
          fullWidth
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={handleCreateEvent}
        >
          {t("eventsAndSpaces.createEvent")}
        </Button>
        {errorMessage && (
          <p className="text-red-500 text-xs pt-2">{errorMessage}</p>
        )}
      </div>
      {newEventIsOpen &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-neutral-green-700 bg-opacity-50 z-[9999]"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setNewEventIsOpen(false);
              }
            }}
          >
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <NewEventForm
                setNewEventIsOpen={setNewEventIsOpen}
                handleFetchEvents={handleFetchEvents}
                setSelectedEvent={setSelectedEvent}
                setEventOpen={setEventOpen}
                eventLength={events.length}
                addProductToEvent={addProductToEvent}
                handleEventClick={handleEventClick}
              />
            </div>
            {popupVisible && (
              <div
                className="absolute top-4 right-4 bg-green-500 text-white p-2 rounded-lg shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                {popupMessage}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default EventDropdown;

function EventItems(event, eventName, selectedEvent, handleEventClick) {
  return (
    <Button
      key={`${event.eventName}-${event.eventDate}`}
      variant="ghost"
      size="md"
      rounded="xl"
      fullWidth
      className={`flex items-center justify-start py-2 px-3 mb-1 ${selectedEvent === event.eventName
        ? "bg-red-300 text-white hover:bg-red-400"
        : "text-gray-400"
        }`}
      onClick={() => handleEventClick("events", event.eventName)}
    >
      <img
        src={event.eventImage || EventImage}
        alt="Event"
        className="w-10 h-10 rounded-xl mr-3 object-cover"
      />
      <div className="flex flex-col items-start">
        <span
          className={`text-sm font-medium ${selectedEvent === event.eventName
            ? "text-grey-100"
            : "text-neutral-green-700"
            } truncate`}
        >
          {event.eventName}
        </span>
        <span className="text-xs flex items-center">
          <Calendar className="h-3 w-3 mr-1" />
          {new Date(event.eventDate).toLocaleDateString()}
        </span>
      </div>
    </Button>
  );
}
